import React, { useMemo } from "react";
import { Box, Typography, Alert, Skeleton } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ReactApexChart from "react-apexcharts";
import { theme } from "../../../../Theme";
import { PROMO_COLORS } from "../../constants/colors";
import { CollapsibleSection } from "./CollapsibleSection";

const TREND_VOLUME_COLOR = theme.palette.primary.main;
const TREND_CM_COLOR = PROMO_COLORS.safe;
const TREND_ROI_COLOR = "#7C3AED";

const fmtTrendVol = (v) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })} L`;

const fmtTrendCm = (v) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 });

const fmtTrendRoi = (v) =>
  v == null || Number.isNaN(Number(v)) ? "—" : `${Number(v).toFixed(2)}×`;

const TREND_TOOLTIP_FORMATTERS = {
  "Volume (L)": fmtTrendVol,
  "Contrib. Margin": fmtTrendCm,
  ROI: fmtTrendRoi,
};

function trendAxisBounds(volSeries, cmSeries, roiSeries) {
  const volCm = [...volSeries, ...cmSeries].map(Number).filter((n) => Number.isFinite(n));
  const roi = roiSeries.map(Number).filter((n) => Number.isFinite(n));
  if (!volCm.length && !roi.length) return null;
  const volCmMax = volCm.length ? Math.max(...volCm) : 0;
  const roiMin = roi.length ? Math.min(...roi) : 0;
  const roiMax = roi.length ? Math.max(...roi) : 0;
  return {
    volCmMin: 0,
    volCmMax: volCmMax > 0 ? Math.ceil(volCmMax * 1.12) : 1,
    roiMin: Math.max(0, Math.floor(roiMin * 0.92 * 100) / 100),
    roiMax: roiMax > roiMin ? Math.ceil(roiMax * 1.08 * 100) / 100 : roiMin + 0.5,
  };
}

function makeMultiSeriesTrendOptions(weekLabels, bounds) {
  const volCmMin = Number.isFinite(bounds.volCmMin) ? bounds.volCmMin : 0;
  const volCmMax =
    Number.isFinite(bounds.volCmMax) && bounds.volCmMax > volCmMin
      ? bounds.volCmMax
      : volCmMin + 1;
  let roiMin = Number.isFinite(bounds.roiMin) ? bounds.roiMin : 0;
  let roiMax = Number.isFinite(bounds.roiMax) ? bounds.roiMax : 1;
  if (roiMax <= roiMin) roiMax = roiMin + 0.5;

  const manyWeeks = weekLabels.length > 14;
  const xLabelStep = manyWeeks ? Math.max(2, Math.ceil(weekLabels.length / 13)) : 1;

  return {
    chart: {
      type: "line",
      fontFamily: "inherit",
      toolbar: {
        show: true,
        offsetY: -4,
        tools: {
          download: false,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
        autoSelected: "zoom",
      },
      zoom: { enabled: true, type: "x" },
      animations: { enabled: true, speed: 400, easing: "easeinout" },
      background: "transparent",
    },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: [2.75, 2.25, 2.5], dashArray: [0, 7, 0] },
    colors: [TREND_VOLUME_COLOR, TREND_CM_COLOR, TREND_ROI_COLOR],
    markers: {
      size: manyWeeks ? 0 : 3,
      strokeWidth: 2,
      strokeColors: "#fff",
      hover: { size: 6, sizeOffset: 2 },
    },
    xaxis: {
      categories: weekLabels,
      axisBorder: { show: true, color: "#E8ECF0" },
      axisTicks: { show: false },
      labels: {
        style: {
          fontSize: manyWeeks ? "10px" : "11px",
          colors: "#94A3B8",
          fontWeight: 500,
        },
        rotate: 0,
        hideOverlappingLabels: true,
        formatter: (val, _ts, opts) => {
          const idx = opts?.i ?? weekLabels.indexOf(val);
          if (idx < 0) return val;
          return idx % xLabelStep === 0 ? val : "";
        },
      },
      title: {
        text: "Calendar week",
        style: { fontSize: "11px", color: "#94A3B8", fontWeight: 500 },
      },
      crosshairs: { stroke: { color: "#CBD5E1", width: 1, dashArray: 4 } },
    },
    yaxis: [
      {
        seriesName: "Volume (L)",
        title: {
          text: "Volume (L) & contrib. margin",
          style: { fontSize: "11px", color: "#64748B", fontWeight: 600 },
        },
        min: volCmMin,
        max: volCmMax,
        forceNiceScale: true,
        tickAmount: 5,
        labels: {
          style: { fontSize: "10px", colors: "#64748B" },
          formatter: (v) =>
            Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 }),
        },
        axisBorder: { show: true, color: "#E8ECF0" },
      },
      {
        seriesName: "Contrib. Margin",
        min: volCmMin,
        max: volCmMax,
        show: false,
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
        title: { show: false },
      },
      {
        seriesName: "ROI",
        opposite: true,
        title: {
          text: "ROI (×)",
          style: { fontSize: "11px", color: TREND_ROI_COLOR, fontWeight: 600 },
        },
        min: roiMin,
        max: roiMax,
        forceNiceScale: true,
        tickAmount: 5,
        labels: {
          style: { fontSize: "10px", colors: TREND_ROI_COLOR },
          formatter: (v) => `${Number(v).toFixed(2)}×`,
        },
        axisBorder: { show: true, color: TREND_ROI_COLOR },
      },
    ],
    grid: {
      borderColor: "#F1F5F9",
      strokeDashArray: 4,
      padding: { left: 12, right: 16, top: 4, bottom: 0 },
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "center",
      offsetY: 4,
      fontSize: "12px",
      fontWeight: 600,
      markers: { radius: 3, width: 12, height: 12, offsetX: -2 },
      itemMargin: { horizontal: 16, vertical: 4 },
      labels: { colors: "#475569" },
    },
    tooltip: {
      theme: "light",
      shared: true,
      intersect: false,
      custom: ({ series, dataPointIndex, w }) => {
        const week = w.globals.labels[dataPointIndex];
        const rows = w.globals.seriesNames
          .map((name, i) => {
            const val = series[i]?.[dataPointIndex];
            const fmtFn = TREND_TOOLTIP_FORMATTERS[name];
            const fmt = fmtFn
              ? fmtFn(val)
              : val == null
              ? "—"
              : Number(val).toFixed(2);
            const color = w.globals.colors[i];
            return `<div style="display:flex;align-items:center;gap:6px;padding:2px 0">
              <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color}"></span>
              <span style="color:#475569;font-size:12px">${name}:</span>
              <span style="font-weight:700;font-size:12px;color:#0F172A">${fmt}</span>
            </div>`;
          })
          .join("");
        return `<div style="padding:10px 14px;min-width:160px;font-family:inherit">
          <div style="font-weight:700;font-size:12px;color:#334155;margin-bottom:8px">${week}</div>
          ${rows}
        </div>`;
      },
    },
  };
}

function HistoricalWeeklyMultiTrendChart({
  weekLabels,
  volSeries,
  cmSeries,
  roiSeries,
  loading,
  height = 400,
}) {
  const series = useMemo(
    () => [
      { name: "Volume (L)", data: volSeries },
      { name: "Contrib. Margin", data: cmSeries },
      { name: "ROI", data: roiSeries },
    ],
    [volSeries, cmSeries, roiSeries]
  );

  const hasData = series.some((s) =>
    s.data.some((v) => v != null && Number.isFinite(Number(v)))
  );

  const axisBounds = useMemo(
    () => (hasData ? trendAxisBounds(volSeries, cmSeries, roiSeries) : null),
    [hasData, volSeries, cmSeries, roiSeries]
  );

  const options = useMemo(
    () => (axisBounds ? makeMultiSeriesTrendOptions(weekLabels, axisBounds) : null),
    [weekLabels, axisBounds]
  );

  if (loading) return <Skeleton variant="rounded" height={height + 48} />;

  if (!hasData || !options) {
    return (
      <Box
        sx={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px dashed #E2E8F0",
          borderRadius: 1.5,
          bgcolor: "#FAFBFC",
        }}
      >
        <Typography variant="caption" color="text.disabled">
          No data for selected week range
        </Typography>
      </Box>
    );
  }

  return (
    <ReactApexChart
      key={`trend-${weekLabels.length}-${axisBounds.volCmMax}-${axisBounds.roiMax}`}
      options={options}
      series={series}
      type="line"
      height={height}
    />
  );
}

export function TrendsSection({
  weeklyPoints,
  loading,
  error,
  globalWeekStart,
  globalWeekEnd,
  filterBar,
}) {
  const lo = Math.min(globalWeekStart, globalWeekEnd);
  const hi = Math.max(globalWeekStart, globalWeekEnd);

  const pointsInRange = useMemo(
    () =>
      weeklyPoints
        .filter((p) => {
          const w = Number(p.week);
          return Number.isFinite(w) && w >= lo && w <= hi;
        })
        .sort((a, b) => a.week - b.week),
    [weeklyPoints, lo, hi]
  );

  const { weekLabels, volSeries, cmSeries, roiSeries } = useMemo(
    () => ({
      weekLabels: pointsInRange.map((p) => p.weekLabel ?? `W${p.week}`),
      volSeries: pointsInRange.map((p) =>
        p.volume != null ? parseFloat(Number(p.volume).toFixed(2)) : null
      ),
      cmSeries: pointsInRange.map((p) =>
        p.cm != null ? parseFloat(Number(p.cm).toFixed(2)) : null
      ),
      roiSeries: pointsInRange.map((p) =>
        p.roi != null ? parseFloat(Number(p.roi).toFixed(4)) : null
      ),
    }),
    [pointsInRange]
  );

  const hasPoints = pointsInRange.some(
    (p) => p.volume != null || p.cm != null || p.roi != null
  );

  return (
    <CollapsibleSection
      icon={<TrendingUpIcon sx={{ fontSize: 20 }} />}
      title="Historical Weekly Trends"
      subtitle="Volume and contribution margin share the left axis; ROI uses the right — dashed green line is CM."
      filterBar={filterBar}
    >
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box
          sx={{
            maxWidth: 1100,
            mx: "auto",
            px: { xs: 2, sm: 3, md: 5 },
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <Skeleton variant="rounded" height={428} />
        </Box>
      ) : !hasPoints ? (
        <Box
          sx={{
            py: 5,
            textAlign: "center",
            border: "1px dashed #E2E8F0",
            borderRadius: 2,
            bgcolor: "#FAFBFC",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No weekly trend data for the selected filters. Adjust brand, year, or
            week range and try again.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            maxWidth: 1100,
            mx: "auto",
            px: { xs: 2, sm: 3, md: 5 },
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <HistoricalWeeklyMultiTrendChart
            key={`trend-multi-${weekLabels.join("|")}`}
            weekLabels={weekLabels}
            volSeries={volSeries}
            cmSeries={cmSeries}
            roiSeries={roiSeries}
            loading={false}
          />
        </Box>
      )}
    </CollapsibleSection>
  );
}
